import { create } from 'zustand';

interface NavigationStore {
    /**
     * Registry of dirty components by unique ID
     */
    dirtyComponents: Set<string>;

    /**
     * Register a component as dirty
     */
    registerDirty: (componentId: string) => void;

    /**
     * Unregister a component from dirty state
     */
    unregisterDirty: (componentId: string) => void;

    /**
     * Check if any component is dirty
     */
    isDirty: () => boolean;

    /**
     * Flag to bypass the next dirty check (for skipDirtyCheck navigation)
     */
    bypassNextCheck: boolean;

    /**
     * Set bypass flag
     */
    setBypassNextCheck: (bypass: boolean) => void;

    /**
     * React Router blocker instance to control navigation
     */
    blocker: any | null;

    /**
     * Set blocker instance
     */
    setBlocker: (blocker: any | null) => void;

    /**
     * Whether the confirmation modal should be shown
     */
    showConfirmation: boolean;

    /**
     * Set confirmation modal visibility
     */
    setShowConfirmation: (show: boolean) => void;

    /**
     * Clear dirty state and proceed with pending navigation
     */
    proceedWithNavigation: () => void;

    /**
     * Cancel navigation and close modal (stay on page)
     */
    cancelNavigation: () => void;

    /**
     * Mobile more options modal state
     */
    isMobileMoreModalOpen: boolean;

    /**
     * Open mobile more options modal
     */
    openMobileMoreModal: () => void;

    /**
     * Close mobile more options modal
     */
    closeMobileMoreModal: () => void;

    /**
     * Toggle mobile more options modal
     */
    toggleMobileMoreModal: () => void;
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
    dirtyComponents: new Set(),
    bypassNextCheck: false,

    registerDirty: (componentId: string) => {
        set(state => {
            const newSet = new Set(state.dirtyComponents);
            newSet.add(componentId);
            return { dirtyComponents: newSet };
        });
    },

    unregisterDirty: (componentId: string) => {
        set(state => {
            const newSet = new Set(state.dirtyComponents);
            newSet.delete(componentId);
            return { dirtyComponents: newSet };
        });
    },

    isDirty: () => {
        // If bypass flag is set, return false to skip dirty check and reset the flag
        if (get().bypassNextCheck) {
            // Reset the flag immediately after checking it
            set({ bypassNextCheck: false });
            return false;
        }
        return get().dirtyComponents.size > 0;
    },

    setBypassNextCheck: (bypass: boolean) => {
        set({ bypassNextCheck: bypass });
    },

    blocker: null,

    setBlocker: (blocker: any | null) => {
        set({ blocker });
    },

    showConfirmation: false,

    setShowConfirmation: (show: boolean) => {
        set({ showConfirmation: show });
    },

    proceedWithNavigation: () => {
        const { blocker } = get();

        // Close modal first
        set({ showConfirmation: false });

        // Execute pending navigation
        if (blocker) {
            blocker.proceed();

            // Clear dirty state and blocker after execution
            set({
                dirtyComponents: new Set(),
                blocker: null,
            });
        }
    },

    cancelNavigation: () => {
        const { blocker } = get();

        // Reset blocker and close modal, keep dirty state
        if (blocker) {
            blocker.reset?.();
        }

        set({
            showConfirmation: false,
            blocker: null,
        });
    },

    isMobileMoreModalOpen: false,

    openMobileMoreModal: () => {
        set({ isMobileMoreModalOpen: true });
    },

    closeMobileMoreModal: () => {
        set({ isMobileMoreModalOpen: false });
    },

    toggleMobileMoreModal: () => {
        set(state => ({ isMobileMoreModalOpen: !state.isMobileMoreModalOpen }));
    },
}));
